// web → apps/api の fetch ラッパ。session cookie を共有するため credentials: 'include'。

import type {
  ButtonEvent,
  ButtonType,
  HostAction,
  RetrospectiveReport,
  SectionMarker,
  Session,
  TrainingTemplate,
} from '@solo-webinar-copilot/shared';
import { API_BASE_URL } from '../env.ts';

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const j = (await res.clone().json()) as { error?: string };
    if (typeof j.error === 'string') return j.error;
  } catch {
    // ignore
  }
  return `HTTP ${res.status}`;
}

async function get<T>(path: string): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true, data: (await res.json()) as T };
}

async function post<T>(path: string): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true, data: (await res.json()) as T };
}

export interface HeatmapBucket {
  minute: number;
  buttonType: ButtonType;
  count: number;
}

export interface SectionStats {
  marker: SectionMarker;
  durationSeconds: number | null;
  doneCount: number;
  pressedByType: Partial<Record<ButtonType, number>>;
}

export interface ParticipantStats {
  participantId: string;
  participantName: string | null;
  pressCount: number;
}

export const fetchSession = (id: string) => get<{ session: Session }>(`/reports/${encodeURIComponent(id)}`);
export const fetchTimeline = (id: string) =>
  get<{ events: ButtonEvent[]; hostActions: HostAction[] }>(
    `/reports/${encodeURIComponent(id)}/timeline`,
  );
export const fetchHeatmap = (id: string) =>
  get<{ buckets: HeatmapBucket[]; startedAt?: number }>(
    `/reports/${encodeURIComponent(id)}/heatmap`,
  );
export const fetchSections = (id: string) =>
  get<{ sections: SectionStats[] }>(`/reports/${encodeURIComponent(id)}/sections`);
export const fetchParticipants = (id: string) =>
  get<{ items: ParticipantStats[] }>(`/reports/${encodeURIComponent(id)}/participants`);

// AI 振り返りサジェスト (M10)。
export interface ImprovementSuggestion {
  category: 'time' | 'difficulty' | 'support' | 'glossary' | 'other';
  message: string;
  evidence?: string;
}

export const fetchSuggestions = (id: string) =>
  get<{ item: RetrospectiveReport | null }>(
    `/reports/${encodeURIComponent(id)}/suggestions`,
  );

export const generateSuggestions = (id: string) =>
  post<{
    ok: boolean;
    summary: string;
    suggestions: ImprovementSuggestion[];
    generatedAt: number;
  }>(`/reports/${encodeURIComponent(id)}/suggestions/generate`);

// ===== dashboard / templates (M11) =====

export interface MeUser {
  id: string;
  email: string;
  displayName: string | null;
  language: string;
}

export const fetchMe = () => get<{ user: MeUser }>('/me');
export const fetchSessions = () => get<{ items: Session[] }>('/sessions');
export const fetchTemplates = () =>
  get<{ items: TrainingTemplate[] }>('/templates');

export async function createTemplate(draft: {
  name: string;
  description?: string;
}): Promise<
  | { ok: true; item: TrainingTemplate }
  | { ok: false; error: string }
> {
  const res = await fetch(`${API_BASE_URL}/templates`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { item: TrainingTemplate };
  return { ok: true, item: j.item };
}

export async function deleteTemplate(
  id: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/templates/${encodeURIComponent(id)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

/** ユーザの言語設定を更新 (UI + AI プロンプト)。 */
export async function updateMyLanguage(
  language: 'ja' | 'en',
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/me`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ language }),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

export interface ComparisonSummary {
  sessionId: string;
  startedAt: number | null;
  endedAt: number | null;
  durationSeconds: number | null;
  totalParticipants: number;
  totalsByButton: Record<string, number>;
}

export const fetchComparisons = (id: string) =>
  get<{ items: ComparisonSummary[]; templateId: string | null }>(
    `/reports/${encodeURIComponent(id)}/comparisons`,
  );

/** セッションに template を紐付け / 解除 (templateId=null で外す)。 */
export async function setSessionTemplate(
  sessionId: string,
  templateId: string | null,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/reports/${encodeURIComponent(sessionId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ templateId }),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

/**
 * Zoom サインイン用 URL。OAuth callback 後に next で指定したページへ戻る。
 * デフォルトは web の現在オリジン /dashboard。
 */
export function loginUrl(next?: string): string {
  const target =
    next ?? (typeof window !== 'undefined' ? `${window.location.origin}/dashboard` : '');
  const u = new URL(`${API_BASE_URL}/oauth/authorize`, 'http://placeholder');
  if (target) u.searchParams.set('next', target);
  // API_BASE_URL は絶対 URL なので URL の origin が変わる。pathname+search を組み立て直す。
  const final = `${API_BASE_URL}/oauth/authorize`;
  return target ? `${final}?next=${encodeURIComponent(target)}` : final;
}

export function logoutUrl(next?: string): string {
  const target =
    next ?? (typeof window !== 'undefined' ? `${window.location.origin}/login` : '');
  const final = `${API_BASE_URL}/oauth/logout`;
  return target ? `${final}?next=${encodeURIComponent(target)}` : final;
}
